/** Returns JWT payload or null if the string is not a decodable JWT. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(
      base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** True when the JWT is missing, unreadable, or past exp (with optional skew). */
export function isJwtExpired(
  token: string | null | undefined,
  skewSeconds = 30
): boolean {
  if (!token) {
    return true;
  }
  const payload = decodeJwtPayload(token);
  const exp = payload?.['exp'];
  if (typeof exp !== 'number') {
    return true;
  }
  const nowSec = Math.floor(Date.now() / 1000);
  return exp <= nowSec + skewSeconds;
}

const AUTH_RETURN_URL_KEY = 'authReturnUrl';
const FORBIDDEN_RETURN_URL_KEY = 'forbiddenReturnUrl';

/** True while Auth0 is completing the OAuth redirect callback. */
export function isAuthCallbackUrl(): boolean {
  const search = window.location.search;
  return /[?&]code=/.test(search) && /[?&]state=/.test(search);
}

const AUTH_URL_PARAMS = [
  'access_token',
  'accessToken',
  'id_token',
  'idToken',
  'token',
  'authToken',
] as const;

const AUTH_COOKIE_NAMES = [
  'access_token',
  'accessToken',
  'id_token',
  'idToken',
  'token',
  'authToken',
  'auth_token',
] as const;

const AUTH_ERROR_PARAMS = ['error', 'error_description', 'state'] as const;

const SILENT_SSO_ATTEMPT_KEY = 'authSilentSsoAttempted';

export function normalizeAuth0Domain(domain: string): string {
  return domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function getStoredAccessToken(): string | null {
  const token =
    localStorage.getItem('accessToken') ||
    localStorage.getItem('access_token') ||
    '';
  return token && !isJwtExpired(token) ? token : null;
}

export function getStoredIdToken(): string | null {
  const token =
    localStorage.getItem('idToken') || localStorage.getItem('id_token') || '';
  return token && !isJwtExpired(token) ? token : null;
}

export function hasValidStoredSession(): boolean {
  return !!getStoredAccessToken();
}

export function getUsernameFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const username =
    payload['username'] ??
    payload['nickname'] ??
    payload['email'] ??
    payload['preferred_username'] ??
    payload['sub'];

  return typeof username === 'string' && username ? username : null;
}

export function getUsernameFromStoredTokens(): string | null {
  return (
    getUsernameFromToken(getStoredIdToken() || '') ||
    getUsernameFromToken(getStoredAccessToken() || '')
  );
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function readAuthTokensFromParams(
  params: URLSearchParams
): { accessToken: string | null; idToken: string | null } {
  const accessToken =
    params.get('access_token') ||
    params.get('accessToken') ||
    params.get('token') ||
    params.get('authToken');
  const idToken = params.get('id_token') || params.get('idToken');

  return { accessToken, idToken };
}

function persistAuthTokens(accessToken: string, idToken?: string | null): void {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('access_token', accessToken);

  if (idToken && !isJwtExpired(idToken)) {
    localStorage.setItem('idToken', idToken);
    localStorage.setItem('id_token', idToken);
  }
}

/** Read griha SSO tokens from the URL query string. */
export function ingestTokensFromUrl(): boolean {
  const { accessToken, idToken } = readAuthTokensFromParams(
    new URLSearchParams(window.location.search)
  );

  if (!accessToken || isJwtExpired(accessToken)) {
    return false;
  }

  persistAuthTokens(accessToken, idToken);
  stripAuthParamsFromUrl();
  return true;
}

/** Read OAuth implicit-flow tokens from the URL hash fragment. */
export function ingestTokensFromHash(): boolean {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) {
    return false;
  }

  const { accessToken, idToken } = readAuthTokensFromParams(
    new URLSearchParams(hash)
  );

  if (!accessToken || isJwtExpired(accessToken)) {
    return false;
  }

  persistAuthTokens(accessToken, idToken);
  stripAuthParamsFromUrl();
  return true;
}

/** Read tokens shared via parent-domain cookies (e.g. tools.grihaindia.org). */
export function ingestTokensFromCookies(): boolean {
  let accessToken: string | null = null;
  let idToken: string | null = null;

  for (const name of AUTH_COOKIE_NAMES) {
    const value = readCookie(name);
    if (!value) {
      continue;
    }

    if (
      !accessToken &&
      (name === 'access_token' ||
        name === 'accessToken' ||
        name === 'token' ||
        name === 'authToken' ||
        name === 'auth_token') &&
      !isJwtExpired(value)
    ) {
      accessToken = value;
    }

    if (
      !idToken &&
      (name === 'id_token' || name === 'idToken') &&
      !isJwtExpired(value)
    ) {
      idToken = value;
    }
  }

  if (!accessToken) {
    return false;
  }

  persistAuthTokens(accessToken, idToken);
  return true;
}

/** Try query string, hash fragment, and shared cookies (griha tools → app SSO). */
export function ingestCrossAppAuthTokens(): boolean {
  return (
    ingestTokensFromUrl() ||
    ingestTokensFromHash() ||
    ingestTokensFromCookies()
  );
}

export function isAuthSilentSsoFailure(): boolean {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error') ?? '';
  return error === 'login_required' || error === 'interaction_required';
}

export function markSilentSsoAttempted(): void {
  sessionStorage.setItem(SILENT_SSO_ATTEMPT_KEY, '1');
}

export function hasSilentSsoBeenAttempted(): boolean {
  return sessionStorage.getItem(SILENT_SSO_ATTEMPT_KEY) === '1';
}

export function clearSilentSsoAttempt(): void {
  sessionStorage.removeItem(SILENT_SSO_ATTEMPT_KEY);
}

export function cameFromGrihaApp(grihaUrl: string): boolean {
  const grihaOrigin = grihaUrl.replace(/\/$/, '');
  const referrer = document.referrer.replace(/\/$/, '');
  return !!referrer && referrer.startsWith(grihaOrigin);
}

export function stripAuthParamsFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;

  [...AUTH_URL_PARAMS, ...AUTH_ERROR_PARAMS].forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (url.hash) {
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    let hashChanged = false;

    AUTH_URL_PARAMS.forEach((key) => {
      if (hashParams.has(key)) {
        hashParams.delete(key);
        hashChanged = true;
      }
    });

    if (hashChanged) {
      const hashQuery = hashParams.toString();
      url.hash = hashQuery ? `#${hashQuery}` : '';
      changed = true;
    }
  }

  if (!changed) {
    return;
  }

  const next =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
    url.hash;
  window.history.replaceState({}, document.title, next);
}

export function buildCrossAppRedirectUrl(targetUrl: string): string {
  const accessToken = getStoredAccessToken();
  if (!accessToken) {
    return targetUrl;
  }

  const url = new URL(targetUrl);
  url.searchParams.set('access_token', accessToken);

  const idToken = getStoredIdToken();
  if (idToken) {
    url.searchParams.set('id_token', idToken);
  }

  return url.toString();
}

export function storeAuthReturnUrl(url: string): void {
  sessionStorage.setItem(AUTH_RETURN_URL_KEY, url);
}

export function peekAuthReturnUrl(): string | null {
  return sessionStorage.getItem(AUTH_RETURN_URL_KEY);
}

export function clearAuthReturnUrl(): void {
  sessionStorage.removeItem(AUTH_RETURN_URL_KEY);
}

export function clearStoredAuthTokens(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('expires_at');
  localStorage.removeItem('scopes');

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('@@auth0spajs@@')) {
      localStorage.removeItem(key);
    }
  });

  sessionStorage.clear();
}

export function clearAuthStateForRelogin(): void {
  const forbiddenReturn = sessionStorage.getItem(FORBIDDEN_RETURN_URL_KEY);
  clearStoredAuthTokens();
  if (forbiddenReturn) {
    sessionStorage.setItem(FORBIDDEN_RETURN_URL_KEY, forbiddenReturn);
  }
}

export function storeForbiddenReturnUrl(url: string): void {
  if (url && !url.includes('forbidden')) {
    sessionStorage.setItem(FORBIDDEN_RETURN_URL_KEY, url);
  }
}

export function peekForbiddenReturnUrl(fallback = '/'): string {
  const stored = sessionStorage.getItem(FORBIDDEN_RETURN_URL_KEY);
  if (stored && !stored.includes('forbidden')) {
    return stored;
  }
  return fallback;
}

export function consumeForbiddenReturnUrl(fallback = '/'): string {
  const stored = sessionStorage.getItem(FORBIDDEN_RETURN_URL_KEY);
  sessionStorage.removeItem(FORBIDDEN_RETURN_URL_KEY);
  if (stored && !stored.includes('forbidden')) {
    return stored;
  }
  return fallback;
}

export function navigateToForbidden(router: {
  url: string;
  navigate: (commands: string[]) => void;
}): void {
  storeForbiddenReturnUrl(router.url);
  router.navigate(['/forbidden']);
}
